/**
 * Synchronous URL-safe ID encryption for resource UUIDs.
 *
 * Algorithm: XOR each UUID byte against a 16-byte key derived from
 * VITE_ID_ENCRYPT_KEY (32 hex chars), then base64url-encode the result.
 * Output is always 22 URL-safe characters — no raw UUIDs ever appear in
 * the browser address bar or server logs.
 *
 * Security: XOR with an unknown key is a Vernam cipher over 16 bytes.
 * Because UUID v4 input bytes are uniformly random, an attacker gains no
 * information without knowing the key. Set VITE_ID_ENCRYPT_KEY to a
 * random 32-hex-char value in each environment.
 *
 * Fallback: if the env var is missing (local dev without .env), the
 * function still works but uses an all-zeros key (no effective hiding).
 */

const RAW_KEY = import.meta.env.VITE_ID_ENCRYPT_KEY ?? '00000000000000000000000000000000';

function buildKey(): Uint8Array {
  const hex = RAW_KEY.replace(/[^0-9a-fA-F]/g, '').slice(0, 32).padEnd(32, '0');
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return bytes;
}

const KEY = buildKey();

function uuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '');
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return bytes;
}

function bytesToUuid(b: Uint8Array): string {
  const h = Array.from(b).map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function toBase64url(b: Uint8Array): string {
  return btoa(String.fromCharCode(...b))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

function fromBase64url(s: string): Uint8Array {
  const pad = (4 - (s.length % 4)) % 4;
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat(pad);
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

/** Encrypt a UUID → 22-char URL-safe string. */
export function encryptId(uuid: string): string {
  const plain = uuidToBytes(uuid);
  const cipher = plain.map((b, i) => b ^ KEY[i]!);
  return toBase64url(cipher);
}

/** Decrypt a 22-char encrypted ID → UUID string. */
export function decryptId(encoded: string): string {
  const cipher = fromBase64url(encoded);
  const plain = cipher.map((b, i) => b ^ KEY[i]!);
  return bytesToUuid(plain);
}

/**
 * True when the string looks like an encrypted ID (22-char base64url).
 * Used as a safety fallback so pages gracefully handle any residual
 * raw UUIDs still in browser history.
 */
export function isEncryptedId(s: string): boolean {
  return /^[A-Za-z0-9_-]{22}$/.test(s);
}

/** Resolve a URL param that may be encrypted or a raw UUID (fallback). */
export function resolveId(param: string): string {
  return isEncryptedId(param) ? decryptId(param) : param;
}
